import { ReactNode } from 'react';
import { Menu, Maximize2, Minimize2 } from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { useResponsiveLayout } from '@/hooks/ui/useResponsiveLayout';
import VersionInfo from '@/components/VersionInfo';

interface MobileHeaderProps {
  /** Dynamic center content (title, breadcrumb, etc.) */
  children?: ReactNode;
  /** Right side actions (buttons, etc.) */
  rightActions?: ReactNode;
  /** Whether to show the fullscreen toggle button */
  showFullscreenToggle?: boolean;
  /** Additional className for the header container */
  className?: string;
}

/**
 * Unified mobile header component for all pages.
 * - Shows hamburger menu button to open sidebar
 * - Shows optional fullscreen toggle button
 * - Accepts dynamic center content and right actions via props
 * - Only visible on mobile (< lg breakpoint) when sidebar is collapsed
 * - Hidden when in fullscreen mode (replaced by a small exit button)
 */
export default function MobileHeader({
  children,
  rightActions,
  showFullscreenToggle = true,
  className = '',
}: MobileHeaderProps) {
  const { isMobile } = useResponsiveLayout();
  const isSidebarCollapsed = useUIStore(state => state.isSidebarCollapsed);
  const setIsSidebarCollapsed = useUIStore(
    state => state.setIsSidebarCollapsed
  );
  const isMobileFullscreen = useUIStore(state => state.isMobileFullscreen);
  const toggleMobileFullscreen = useUIStore(
    state => state.toggleMobileFullscreen
  );

  // Only show on mobile when sidebar is collapsed and not in fullscreen mode
  if (!isMobile || !isSidebarCollapsed || isMobileFullscreen) return null;

  return (
    <div
      className={`sticky top-0 z-20 flex items-center h-12 px-3 bg-white border-b border-gray-200 lg:hidden ${className}`}
    >
      {/* Left: Hamburger menu */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          aria-label="Open sidebar"
          className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          onClick={() => setIsSidebarCollapsed(false)}
        >
          <Menu size={20} />
        </button>
      </div>

      {/* Center: Dynamic content - supports horizontal scrolling for breadcrumb */}
      {children && (
        <div className="flex-1 min-w-0 mx-2 overflow-x-auto scrollbar-hide">
          {children}
        </div>
      )}

      {/* Right: Actions + VersionInfo + Fullscreen toggle */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {rightActions}
        <VersionInfo />
        {showFullscreenToggle && (
          <button
            type="button"
            aria-label="Enter fullscreen"
            className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={toggleMobileFullscreen}
          >
            <Maximize2 size={16} className="-rotate-45" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Fullscreen exit button - shows a small floating button in the top-right corner
 * when in mobile fullscreen mode. Click to exit fullscreen.
 */
export function MobileFullscreenExitButton() {
  const { isMobile } = useResponsiveLayout();
  const isMobileFullscreen = useUIStore(state => state.isMobileFullscreen);
  const toggleMobileFullscreen = useUIStore(
    state => state.toggleMobileFullscreen
  );

  if (!isMobile || !isMobileFullscreen) return null;

  return (
    <button
      type="button"
      aria-label="Exit fullscreen"
      className="fixed top-0 right-0 z-50 p-1.5 bg-white/70 backdrop-blur-sm text-gray-500 hover:text-gray-800 hover:bg-white/90 rounded-bl-lg shadow-sm transition-all border-b border-l border-gray-200/50"
      onClick={toggleMobileFullscreen}
    >
      <Minimize2 size={14} className="-rotate-45" />
    </button>
  );
}
